/**
 * FloatingPreview — draggable/resizable iframe of the Homepage. Refreshes
 * automatically when the config is saved (via `savedAt` bump). Can be
 * collapsed to a small tab in the corner. Uses postMessage → PREVIEW_CFG to
 * push live edits into Home.jsx without hitting the backend.
 */
import React, { useEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";

export function FloatingPreview({ cfg, savedAt, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(480);
  const [height, setHeight] = useState(320);
  const [pos, setPos] = useState({ right: 24, bottom: 90 });
  const [device, setDevice] = useState("desktop");
  const iframeRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const isMobile = device === "mobile";
  const iframeInnerWidth  = isMobile ? 375  : 1920;
  const iframeInnerHeight = isMobile ? 812  : 1200;
  const bodyH = height - 65;
  const scaleFit = Math.min(width / iframeInnerWidth, bodyH / iframeInnerHeight);

  useEffect(() => {
    if (savedAt && iframeRef.current) {
      iframeRef.current.src = `${backendUrl}/?_t=${savedAt}&preview=1`;
    }
  }, [savedAt, backendUrl]);

  useEffect(() => {
    if (!open || !cfg || !iframeRef.current) return;
    const id = setTimeout(() => {
      try {
        iframeRef.current?.contentWindow?.postMessage({ type: "PREVIEW_CFG", cfg }, "*");
      } catch (err) { console.debug("preview postMessage failed", err); }
    }, 120);
    return () => clearTimeout(id);
  }, [cfg, open]);

  const handleLoad = () => {
    try {
      iframeRef.current?.contentWindow?.postMessage({ type: "PREVIEW_CFG", cfg }, "*");
    } catch (err) { console.debug("preview postMessage failed", err); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 z-50 bg-purple-600 hover:bg-purple-700 text-white font-[Fredoka] font-bold rounded-full shadow-2xl px-5 py-3 flex items-center gap-2 transition hover:scale-105"
        style={{ right: 220, boxShadow: "0 12px 32px -6px rgba(147,51,234,0.55)" }}
        title="Abrir preview da Homepage"
        data-testid="floating-preview-open"
      >
        <Eye className="w-5 h-5" />
        <span className="text-sm">Preview</span>
      </button>
    );
  }

  return (
    <div
      className="fixed z-40 rounded-xl overflow-hidden shadow-2xl border-2 border-purple-500 bg-slate-900"
      style={{ right: pos.right, bottom: pos.bottom, width, height, boxShadow: "0 20px 60px -10px rgba(0,0,0,0.65)" }}
      data-testid="floating-preview-panel"
    >
      <div className="bg-purple-700 text-white px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4" />
          <span className="text-xs font-black uppercase tracking-wide">Preview Homepage · Live</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onSave && onSave()}
            disabled={saving}
            className="text-[10px] font-black bg-white/10 hover:bg-white/25 rounded px-2 py-1 disabled:opacity-50"
            title="Guardar e recarregar preview"
            data-testid="floating-preview-save"
          >
            {saving ? "A guardar…" : "Guardar+Recarregar"}
          </button>
          <button
            onClick={() => { if (iframeRef.current) iframeRef.current.src = `${backendUrl}/?_t=${Date.now()}`; }}
            className="text-[10px] font-black bg-white/10 hover:bg-white/25 rounded px-2 py-1"
            title="Recarregar preview"
            data-testid="floating-preview-refresh"
          >
            ↻
          </button>
          <button
            onClick={() => setOpen(false)}
            className="text-xs font-black bg-white/10 hover:bg-red-500/70 rounded px-2 py-1"
            title="Fechar"
            data-testid="floating-preview-close"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="bg-slate-800 text-white px-2 py-1 flex items-center gap-1 text-[10px] border-b border-slate-700 flex-wrap">
        <span className="opacity-60 mr-1">Ecrã:</span>
        <button
          onClick={() => setDevice("desktop")}
          className={`px-2 py-0.5 rounded font-bold ${device === "desktop" ? "bg-purple-500 text-white" : "bg-slate-700 hover:bg-slate-600"}`}
          title="Desktop 1920×1200"
          data-testid="preview-device-desktop"
        >
          🖥️ Desktop
        </button>
        <button
          onClick={() => setDevice("mobile")}
          className={`px-2 py-0.5 rounded font-bold ${device === "mobile" ? "bg-purple-500 text-white" : "bg-slate-700 hover:bg-slate-600"}`}
          title="Mobile 375×812 (iPhone)"
          data-testid="preview-device-mobile"
        >
          📱 Mobile
        </button>
        <span className="opacity-60 ml-2 mr-1">Tamanho:</span>
        <button onClick={() => { setWidth(360); setHeight(240); }} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600" title="Pequeno">S</button>
        <button onClick={() => { setWidth(480); setHeight(320); }} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600" title="Médio">M</button>
        <button onClick={() => { setWidth(640); setHeight(420); }} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600" title="Grande">L</button>
        <button onClick={() => { setWidth(900); setHeight(560); }} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600" title="Muito grande">XL</button>
        <span className="opacity-60 ml-2 mr-1">Posição:</span>
        <button onClick={() => setPos({ right: 24, bottom: 90 })} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600">↘</button>
        <button onClick={() => setPos({ right: 24, bottom: window.innerHeight - height - 100 })} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600">↗</button>
        <button onClick={() => setPos({ right: window.innerWidth - width - 24, bottom: 90 })} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600">↙</button>
        <span className="ml-auto opacity-60 text-[9px]">{width}×{height}px · {Math.round(scaleFit*100)}%</span>
      </div>

      <div className="relative bg-slate-950 overflow-hidden flex items-start justify-center" style={{ width: "100%", height: bodyH }}>
        <iframe
          ref={iframeRef}
          src={`${backendUrl}/?_t=${savedAt || Date.now()}&preview=1`}
          title="Preview Homepage"
          onLoad={handleLoad}
          className="border-0"
          style={{
            width: iframeInnerWidth,
            height: iframeInnerHeight,
            transform: `scale(${scaleFit})`,
            transformOrigin: "top center",
            background: "#0A0E2A",
            flexShrink: 0,
          }}
          data-testid="floating-preview-iframe"
        />
      </div>
    </div>
  );
}
